import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, CircularProgress } from '@material-ui/core';

import { ApiParams } from '../../common/api/api';
import { createErrorMetadataView } from '../../common/views/ErrorMetadataView';
import AppSnackbar from '../../common/AppSnackbar';
import { UserContext } from '../../user/User.context';
import { UpdateRelatedEntityStatusErrorPlugin } from '../workflow/plugins/services/UpdateRelatedEntityStatusPlugin';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { compareTaskStatus } from './task.helper';
import { useFindTask, useUpdateTask } from './Task.hooks';
import { Task, TaskEntity } from './Task.interface';
import { useGetWorkflowMachineState } from './Task.machine';

const ErrorMetadataView = createErrorMetadataView(
  UpdateRelatedEntityStatusErrorPlugin
);

const StyledStatusActions = styled.div`
  display: flex;
  align-items: center;
`;

export interface TaskStatusActionsProps {
  task: Task;
  taskEntity: TaskEntity;
  disabled?: boolean;
  onChange?: (task: Task) => void;
  onBeforeUpdateTask?: (task: Task, updateTask: () => void) => void;
}

const TaskStatusActions: React.FC<TaskStatusActionsProps> = ({
  task: initialTask,
  taskEntity,
  disabled,
  onChange,
  onBeforeUpdateTask
}) => {
  const { user } = useContext(UserContext);
  const [task, setTask] = useState({ ...initialTask });
  const { stateNode, render, canTransition, handleTransition } =
    useGetWorkflowMachineState(task);
  const {
    updatedTask,
    isUpdating,
    updateError,
    updateTask: updateTaskCb
  } = useUpdateTask(initialTask.id || 0);
  // trigger no workflow warning
  useTaskEntityFields(task);

  // filter active and assigned subTasks
  // inactive subTasks may be present with active ones
  // since older subTasks can now be kept from previous states
  const assignedSubTasks = (task.children || []).filter(subTask => {
    return (
      user &&
      subTask.user &&
      user.id === subTask.user.id &&
      (!subTask.statePath || compareTaskStatus(subTask.statePath, task.status))
    );
  });

  const updateTask = (task: Task, params?: ApiParams) => {
    if (onBeforeUpdateTask) {
      onBeforeUpdateTask(task, () => updateTaskCb(params));
    } else {
      updateTaskCb(params);
    }
  };

  // update task notify parent component
  useEffect(() => {
    if (updatedTask) {
      setTask(updatedTask);
      onChange?.(updatedTask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  // update task state on initialTask and initialTask jiraIssues update
  useEffect(() => {
    setTask({ ...initialTask });
  }, [initialTask, initialTask.children, initialTask?.jiraIssues]);

  // for reloading the task if subtask is updated
  const {
    task: loadedTask,
    isLoading,
    findError,
    findTask
  } = useFindTask(task.id!);
  useEffect(() => {
    if (loadedTask) {
      setTask(loadedTask);
      onChange?.(loadedTask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedTask]);

  return (
    <StyledStatusActions>
      {stateNode?.events?.map(event => {
        const isDisabled =
          disabled ||
          isUpdating ||
          isLoading ||
          !canTransition(event, task, taskEntity, user!);
        const isVisible =
          assignedSubTasks.length === 0 ||
          (assignedSubTasks.length > 0 && !isDisabled);
        return (
          isVisible &&
          event.name && (
            <Button
              key={event.name}
              variant='contained'
              color='primary'
              disabled={isDisabled}
              onClick={() => {
                const oldStatePath = stateNode?.path;
                handleTransition({ event, task, taskEntity, user: user! })
                  .then(newState => {
                    if (!newState || newState?.path === oldStatePath) {
                      return;
                    }
                    updateTask(task, {
                      status: newState.path,
                      event: event.toJson(),
                      reason: newState.context.reason,
                      subTaskUserIds: newState.context.subTaskUserIds,
                      pluginData: newState.context.pluginData
                    });
                  })
                  .catch(error => {
                    console.error(error);
                  });
              }}
            >
              {event.name}
            </Button>
          )
        );
      })}
      {assignedSubTasks.map(subTask => (
        <TaskStatusActions
          key={subTask.id}
          task={subTask}
          taskEntity={taskEntity}
          disabled={disabled}
          onChange={async subTask => {
            const taskKey = task.key || `TASK-${task.id}`;
            const subTaskKey = subTask.key || `SUBTASK-${subTask.id}`;
            // update subTask in task.children
            // so that checks with subTask statuses will succeed
            if (!task.children) {
              task.children = [];
            }
            const index = task.children.findIndex(
              child => child.id === subTask.id
            );
            if (index === -1) {
              console.debug(
                '[actions] %s (%o) push %s (%o)',
                taskKey,
                task.status,
                subTaskKey,
                subTask.status
              );
              task.children.push(subTask);
            } else {
              console.debug(
                '[actions] %s (%o) update %s (%o->%o)',
                taskKey,
                task.status,
                subTaskKey,
                task.children[index].status,
                subTask.status
              );
              Object.assign(task.children[index], subTask);
            }
            // after updating subTasks, run transient events
            // for parent task to trigger state invoke actions
            try {
              // no need to call updateTask() since parent tasks
              // are already updated by the subTask's updateTask() call
              const oldStatePath = stateNode?.path;
              const newState = await handleTransition({
                task,
                taskEntity,
                user: user!,
                event: undefined
              });
              console.debug(
                '[actions] %s transition %o->%o',
                taskKey,
                oldStatePath,
                newState?.path
              );
            } catch (error) {
              console.error(
                '[actions] %s (%o) %s (%o) error: %o',
                taskKey,
                task.status,
                subTaskKey,
                subTask.status,
                error
              );
            } finally {
              // reload the parent task
              findTask();
            }
          }}
        />
      ))}
      {(isUpdating || isLoading) && <CircularProgress size={20} />}
      {render}
      <AppSnackbar severity='error' open={Boolean(updateError || findError)}>
        {updateError?.message ||
          findError?.message ||
          'There was an error updating the status.'}
      </AppSnackbar>
      <ErrorMetadataView error={updateError} />
    </StyledStatusActions>
  );
};

export default TaskStatusActions;
